import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
    OmFormInputFieldWrapper,
    OmMobileInputFieldWrapper,
    OmFormDropdownFieldWrapper,
    OmEfpPersonalDetails,
} from '@om/component-library-react';
import { FORM_STATUS } from '@om/redux/reducers/status';

import {
    setCityValue,
    setComplexValue,
    setCountryValue,
    setEmailValue,
    setIdValue,
    setPostalAddressSameValue,
    setPostalCodeValue,
    setStreetAddressValue,
    setSuburbValue,
    setFirstNamesValue,
    setSurnameValue,
    setMobileValue,
} from '@om/redux/actions/easiplusFuneralPlan/personalDetails';
import { setFirstNamesValue as thankYouSetFirstNamesValue } from '@om/redux/actions/easiplusFuneralPlan/thankYou';
import {
    getCity,
    getComplex,
    getCountry,
    getEmail,
    getFirstNames,
    getId,
    getMobile,
    getPostalCode,
    getStreetAddress,
    getSuburb,
    getSurname,
} from '@om/redux/selectors/easiplusFuneralPlan/personalDetails';
import {
    getReferencePostalMethods,
    getReferenceNationalities,
} from '@om/redux/selectors/easiplusFuneralPlan/referenceData';
import { DEFAULT_VALIDATION_DELAY } from '../../config';

const PersonalDetailsPage = () => {
    const formFieldProperties = {
        required: true,
        size: 'full',
        formId: 'personalDetailsForm',
        align: 'left',
        validationDelay: DEFAULT_VALIDATION_DELAY,
    };

    const dropdownFieldProperties = {
        useNativeMobileDropdown: true,
        required: true,
        formId: 'personalDetailsForm',
        align: 'left',
    };

    const dispatch = useDispatch();
    const {
        city,
        complex,
        country,
        email,
        firstNames,
        id,
        mobile,
        postalCode,
        streetAddress,
        suburb,
        surname,
        referenceNationalities,
    } = useSelector(
        createStructuredSelector({
            city: getCity,
            complex: getComplex,
            country: getCountry,
            email: getEmail,
            firstNames: getFirstNames,
            id: getId,
            mobile: getMobile,
            postalCode: getPostalCode,
            streetAddress: getStreetAddress,
            suburb: getSuburb,
            surname: getSurname,
            referenceNationalities: getReferenceNationalities,
        })
    );

    useEffect(() => {
        setPostalAddressSameValue('1');
    });

    return (
        <OmEfpPersonalDetails>
            <h5 slot="heading" className="heading">
                Let’s get to know you
            </h5>
            <p slot="subheading">
                To help us process your application and give you the best service, we need some details from you.
            </p>

            <div slot="form">
                <section className="form-section">
                    <h6 className="no-bottom-margin">
                        <strong>Your personal details</strong>
                    </h6>

                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="firstNames"
                        name="name"
                        placeholder="First name(s)"
                        value={firstNames.displayValue}
                        state={firstNames.status}
                        errorMessage={firstNames.error}
                        onPersist={(event) => {
                            dispatch(setFirstNamesValue(event.detail));
                            dispatch(thankYouSetFirstNamesValue(event.detail));
                        }}
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="surname"
                        name="surname"
                        placeholder="Surname"
                        value={surname.displayValue}
                        state={surname.status}
                        errorMessage={surname.error}
                        onPersist={(event) => dispatch(setSurnameValue(event.detail))}
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="id"
                        placeholder="South African ID number"
                        name="id"
                        hintText="E.g. 790210111111"
                        keyboardMode="numeric"
                        value={id.displayValue}
                        state={id.status}
                        errorMessage={id.error}
                        onPersist={(event) => dispatch(setIdValue(event.detail))}
                    />

                    <h6 className="no-bottom-margin">
                        <strong>Contact details</strong>
                    </h6>

                    <div className="mobile-field">
                        <OmMobileInputFieldWrapper
                            id="mobileNumber"
                            formId="personalDetailsForm"
                            placeholderText="Contact Number"
                            validationDelay={DEFAULT_VALIDATION_DELAY}
                            value={mobile.displayValue}
                            state={mobile.status}
                            errorMessage={mobile.error}
                            onPersist={(event) => dispatch(setMobileValue(event.detail))}
                            hintText={
                                mobile.status === FORM_STATUS.VALID
                                    ? ''
                                    : 'Remember to add “0” in front of your mobile number. E.g. 082 123 4567'
                            }
                        />
                    </div>
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="email"
                        placeholder="Email"
                        name="email"
                        value={email.displayValue}
                        state={email.status}
                        errorMessage={email.error}
                        onPersist={(event) => dispatch(setEmailValue(event.detail))}
                    />

                    <h6 className="no-bottom-margin">
                        <strong>Residential address details</strong>
                    </h6>
                    <p>
                        PO BOX or Private Bag addresses will not be accepted as a residential address. This will result
                        in your application being declined.
                    </p>
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="complex"
                        placeholder="Complex/building"
                        name="complex"
                        value={complex.value}
                        state={complex.status}
                        errorMessage={complex.error}
                        onPersist={(event) => dispatch(setComplexValue(event.detail))}
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="streetAddress"
                        placeholder="Street address"
                        name="streetAddress"
                        value={streetAddress.value}
                        state={streetAddress.status}
                        errorMessage={streetAddress.error}
                        onPersist={(event) => dispatch(setStreetAddressValue(event.detail))}
                        hintText="Only a street address is allowed."
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="suburb"
                        placeholder="Suburb"
                        name="suburb"
                        value={suburb.value}
                        state={suburb.status}
                        errorMessage={suburb.error}
                        onPersist={(event) => dispatch(setSuburbValue(event.detail))}
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        id="city"
                        placeholder="City"
                        name="city"
                        value={city.value}
                        state={city.status}
                        errorMessage={city.error}
                        onPersist={(event) => dispatch(setCityValue(event.detail))}
                    />
                    <OmFormInputFieldWrapper
                        {...formFieldProperties}
                        isSensitive
                        id="postalCode"
                        placeholder="Postal code"
                        name="postalCode"
                        value={postalCode.displayValue}
                        state={postalCode.status}
                        errorMessage={postalCode.error}
                        onPersist={(event) => dispatch(setPostalCodeValue(event.detail))}
                    />
                    <OmFormDropdownFieldWrapper
                        {...dropdownFieldProperties}
                        size="full"
                        id="country"
                        name="country"
                        placeholder="Country"
                        disabled="true"
                        filterable="true"
                        errorMessagePopupAlignment="left"
                        value={country.value}
                        onOnSelect={(event) => dispatch(setCountryValue(event.detail.value))}
                        options={
                            referenceNationalities &&
                            JSON.stringify(
                                referenceNationalities.map((referenceNationality) => ({
                                    value: referenceNationality.code,
                                    label: referenceNationality.description,
                                }))
                            )
                        }
                    />
                </section>
            </div>
        </OmEfpPersonalDetails>
    );
};

export default PersonalDetailsPage;
